/**
 * Blocos interativos: CTA, Video, Countdown (contador eleitoral), Testimonials.
 */

/* eslint-disable react-refresh/only-export-components */

import { useEffect, useState } from 'react';
import { MessageSquare, Video as VideoIcon, Clock, Quote } from 'lucide-react';
import type { BlockDefinition, BlockRenderProps } from './types';
import { BlockShell } from './BlockShell';

// =============================================================================
// CTABlock
// =============================================================================

interface CTAProps {
  headline?: string;
  subheadline?: string;
  buttonText?: string;
  buttonUrl?: string;
}

function CTARender({ block, ctx }: BlockRenderProps) {
  const props = block.props as CTAProps;
  if (!props.buttonText || !props.buttonUrl) return null;

  const theme = ctx.tenant.theme;

  return (
    <BlockShell block={block} ctx={ctx}>
      <div className="text-center max-w-2xl mx-auto">
        {props.headline && (
          <h2 className="font-display text-3xl md:text-5xl font-extrabold mb-4">
            {props.headline}
          </h2>
        )}
        {props.subheadline && (
          <p className="text-lg md:text-xl opacity-80 mb-8">{props.subheadline}</p>
        )}
        <a
          href={props.buttonUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-10 py-5 rounded-2xl font-display text-lg font-bold bg-white shadow-xl hover:scale-105 transition-transform"
          style={{ color: theme.primaryColor }}
        >
          {props.buttonText}
        </a>
      </div>
    </BlockShell>
  );
}

export const ctaBlockDefinition: BlockDefinition<CTAProps> = {
  type: 'cta',
  label: 'Chamada para ação',
  description: 'Botao destacado com headline',
  category: 'interaction',
  multiple: true,
  icon: MessageSquare,
  defaultProps: {
    headline: 'Vamos transformar nossa cidade?',
    buttonText: 'Falar comigo',
  },
  Render: CTARender,
};

// =============================================================================
// VideoBlock — YouTube/Vimeo embed
// =============================================================================

interface VideoProps {
  title?: string;
  url?: string;
  aspect?: '16:9' | '9:16';
}

function extractEmbedUrl(url: string): string | null {
  if (!url) return null;
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
}

function VideoRender({ block, ctx }: BlockRenderProps) {
  const props = block.props as VideoProps;
  if (!props.url) return null;
  const embedUrl = extractEmbedUrl(props.url);
  if (!embedUrl) return null;

  const theme = ctx.tenant.theme;
  const aspectClass = props.aspect === '9:16' ? 'aspect-[9/16] max-w-md mx-auto' : 'aspect-video';

  return (
    <BlockShell block={block} ctx={ctx}>
      <div className="max-w-4xl mx-auto">
        {props.title && (
          <h2
            className="font-display text-3xl md:text-4xl font-extrabold mb-6"
            style={{ color: theme.primaryColor }}
          >
            {props.title}
          </h2>
        )}
        <div className={`${aspectClass} rounded-2xl overflow-hidden shadow-xl bg-black`}>
          <iframe
            src={embedUrl}
            title={props.title || 'Vídeo'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full border-0"
          />
        </div>
      </div>
    </BlockShell>
  );
}

export const videoBlockDefinition: BlockDefinition<VideoProps> = {
  type: 'video',
  label: 'Vídeo',
  description: 'Embed YouTube ou Vimeo',
  category: 'visual',
  multiple: true,
  icon: VideoIcon,
  defaultProps: { aspect: '16:9' },
  Render: VideoRender,
};

// =============================================================================
// CountdownBlock — contador para data da eleicao
// =============================================================================

interface CountdownProps {
  title?: string;
  targetDate?: string; // ISO date
  finishedMessage?: string;
}

function useCountdown(target: string | undefined) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  if (!target) return null;
  const t = new Date(target).getTime();
  if (Number.isNaN(t)) return null;
  const diff = t - now;
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, finished: true };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    finished: false,
  };
}

function CountdownRender({ block, ctx }: BlockRenderProps) {
  const props = block.props as CountdownProps;
  const result = useCountdown(props.targetDate);
  if (!result) return null;

  const theme = ctx.tenant.theme;

  if (result.finished) {
    return (
      <BlockShell block={block} ctx={ctx}>
        <div className="text-center">
          <h2
            className="font-display text-3xl md:text-4xl font-extrabold"
            style={{ color: theme.primaryColor }}
          >
            {props.finishedMessage || 'É hoje!'}
          </h2>
        </div>
      </BlockShell>
    );
  }

  return (
    <BlockShell block={block} ctx={ctx}>
      <div className="text-center">
        {props.title && (
          <p className="text-sm md:text-base font-semibold uppercase tracking-widest text-ink-500 mb-4">
            {props.title}
          </p>
        )}
        <div className="inline-flex items-center gap-4 md:gap-8">
          <CountdownCell value={result.days} label="dias" color={theme.primaryColor} />
          <CountdownCell value={result.hours} label="horas" color={theme.primaryColor} />
          <CountdownCell value={result.minutes} label="min" color={theme.primaryColor} />
        </div>
      </div>
    </BlockShell>
  );
}

function CountdownCell({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div className="text-center">
      <div
        className="font-display text-5xl md:text-7xl font-extrabold tabular-nums"
        style={{ color }}
      >
        {String(value).padStart(2, '0')}
      </div>
      <div className="text-sm md:text-base font-semibold uppercase tracking-wider text-ink-500 mt-2">
        {label}
      </div>
    </div>
  );
}

export const countdownBlockDefinition: BlockDefinition<CountdownProps> = {
  type: 'countdown',
  label: 'Contador eleitoral',
  description: 'Contagem regressiva ate o dia da eleicao',
  category: 'interaction',
  multiple: false,
  icon: Clock,
  defaultProps: {
    title: 'Faltam para o dia da eleição',
    finishedMessage: 'Hoje é dia de votar!',
  },
  Render: CountdownRender,
};

// =============================================================================
// TestimonialsBlock
// =============================================================================

interface Testimonial {
  quote: string;
  author: string;
  role?: string;
}

interface TestimonialsProps {
  title?: string;
  items?: Testimonial[];
}

function TestimonialsRender({ block, ctx }: BlockRenderProps) {
  const props = block.props as TestimonialsProps;
  const items = props.items || [];
  if (items.length === 0) return null;

  const theme = ctx.tenant.theme;

  return (
    <BlockShell block={block} ctx={ctx}>
      <div>
        {props.title && (
          <h2
            className="font-display text-3xl md:text-4xl font-extrabold mb-8"
            style={{ color: theme.primaryColor }}
          >
            {props.title}
          </h2>
        )}
        <div className="grid md:grid-cols-2 gap-6">
          {items.map((t, i) => (
            <figure
              key={i}
              className="bg-white rounded-2xl p-6 shadow-sm border border-ink-100"
            >
              <Quote
                className="h-8 w-8 mb-3 opacity-30"
                style={{ color: theme.primaryColor }}
              />
              <blockquote className="text-lg text-ink-700 leading-relaxed italic">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-4">
                <div className="font-display font-bold text-ink-900">{t.author}</div>
                {t.role && <div className="text-sm text-ink-500">{t.role}</div>}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </BlockShell>
  );
}

export const testimonialsBlockDefinition: BlockDefinition<TestimonialsProps> = {
  type: 'testimonials',
  label: 'Depoimentos',
  description: 'Citacoes de apoiadores',
  category: 'content',
  multiple: true,
  icon: Quote,
  defaultProps: { title: 'O que dizem sobre mim', items: [] },
  Render: TestimonialsRender,
};
